import type { QueryClient } from '@tanstack/react-query';

/**
 * Background page preloading that never competes with the page on screen.
 *
 * The shell fetches the code of the pages a person is likely to open next.
 * Started on the first idle moment, it ran before the current page had even
 * asked for its own code: a report opened from a link queued its chunks behind
 * ~150 others and drew its first figure five seconds later. So preloading
 * waits for quiet (no page code downloading, no query fetching) and then
 * fetches ONE page at a time, waiting for quiet again in between, so a click
 * made meanwhile still gets the network to itself.
 */

let pendingPageLoads = 0;

/** Counts a page's code download (a real navigation, never a preload). */
export function trackPageLoad<T>(load: Promise<T>): Promise<T> {
  pendingPageLoads++;
  return load.finally(() => {
    pendingPageLoads--;
  });
}

interface Preloadable {
  preload: () => Promise<unknown>;
}

/** How long the app must stay quiet before the next page is fetched. */
const QUIET_MS = 800;
const POLL_MS = 100;

function isQuiet(queryClient: QueryClient) {
  return pendingPageLoads === 0 && queryClient.isFetching() === 0;
}

function nextIdle(): Promise<void> {
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window)
      window.requestIdleCallback(() => resolve(), { timeout: 2000 });
    else setTimeout(resolve, 0);
  });
}

/**
 * Fetches each page's code in turn, each once the app has been quiet for
 * QUIET_MS. Returns a cancel function. Already-fetched pages cost nothing:
 * the module import resolves from cache.
 */
export function preloadWhenQuiet(
  pages: readonly Preloadable[],
  queryClient: QueryClient,
): () => void {
  let cancelled = false;
  const waitForQuiet = async () => {
    let quietSince = 0;
    while (!cancelled) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      if (!isQuiet(queryClient)) quietSince = 0;
      else if (!quietSince) quietSince = Date.now();
      else if (Date.now() - quietSince >= QUIET_MS) return;
    }
  };
  void (async () => {
    for (const page of pages) {
      await waitForQuiet();
      if (cancelled) return;
      await nextIdle();
      if (cancelled) return;
      await page.preload();
    }
  })();
  return () => {
    cancelled = true;
  };
}
